import { Box, Stack, Typography } from '@mui/material';
import CurvedArrow from 'assets/icons/curved-arrow.svg';
import {
  BINARY_CONSTANTS,
  DEFAULT_AUTOPAY_TOGGLE_BUTTONS_GROUP_OPTIONS_NEW,
} from 'common/constants';
import { FormControl } from 'components/lib/FormControl';
import type { IToggleButton } from 'components/lib/ToggleButton/ToggleButton.types';
import { ToggleButtonsGroup } from 'components/lib/ToggleButtonsGroup';
import { SvgIconWrapper } from 'components/SvgIconWrapper';
import type { FormikProps } from 'formik';
import type { FC, MouseEvent } from 'react';
import type { IPaymentFormikValues } from './CheckoutForm';

interface PaymentFrequencyProps {
  formik: FormikProps<IPaymentFormikValues>;
  isLoading: boolean;
}

const PaymentFrequency: FC<PaymentFrequencyProps> = ({ formik, isLoading }) => {
  const AUTO_PAY_MESSAGES = {
    [BINARY_CONSTANTS.YES]: (
      <>
        By selecting{' '}
        <Box component="span" fontWeight={600}>
          “Enroll in Autopay”
        </Box>
        , I also authorize THREE to automatically debit this payment method for future insurance
        payments when they become due. I understand that a $10.00 late fee applies to each
        installment paid more than 5 days after due date.
      </>
    ),
    [BINARY_CONSTANTS.NO]: (
      <>
        By selecting{' '}
        <Box component="span" fontWeight={600}>
          “Single Payment”
        </Box>
        , I agree to make monthly nonrecurring payments. I understand that a $7.00 fee applies to
        each monthly installment for customers not enrolled in autopay and that a $10.00 late fee
        applies to each installment paid more than 5 days after due date.
      </>
    ),
  };

  const renderAutoPayOptionText = (autoPayOption: string) => {
    const message = AUTO_PAY_MESSAGES[autoPayOption];

    if (!message) return null;

    return (
      <Box
        sx={{
          borderLeft: '6px solid',
          borderRadius: '4px',
          borderColor: (theme) => theme.palette.customColors.copper,
          backgroundColor: (theme) => theme.palette.customColors.lightYellow,
        }}
      >
        <Typography variant="body2" py={1.5} px={3} fontStyle="italic">
          {message}
        </Typography>
      </Box>
    );
  };

  const handleOnChangeAutoPayToggleButtonGroups = (
    _: MouseEvent<HTMLElement>,
    newValue: string,
  ) => {
    if (newValue === null) return;

    formik.setValues({
      ...formik.values,
      is_recurring: newValue,
    });
  };

  return (
    <FormControl
      error={formik.touched.is_recurring && Boolean(formik.errors.is_recurring)}
      errorMessage={String(formik.errors.is_recurring)}
      sx={{
        display: 'flex',
        pt: 6,
        pb: 4,
      }}
    >
      <Stack gap={2}>
        <Typography variant="h3">Select payment frequency</Typography>

        <ToggleButtonsGroup
          options={DEFAULT_AUTOPAY_TOGGLE_BUTTONS_GROUP_OPTIONS_NEW as IToggleButton[]}
          onChange={handleOnChangeAutoPayToggleButtonGroups}
          value={formik.values.is_recurring}
          disabled={isLoading}
          sx={{
            gap: 0,
            '& .MuiButtonBase-root': {
              py: '10px',
              px: 2,
              minHeight: 'fit-content',
              borderRadius: '4px',
            },
            '& .MuiToggleButtonGroup-firstButton': {
              transform: 'translateX(1px)',
            },
            '& .MuiButtonBase-root.MuiToggleButton-root.Mui-selected ': {
              background: (theme) => theme.palette.customColors.lightYellow,
              zIndex: 2,
              '.common-icon-style svg path': {
                stroke: (theme) => theme.palette.customColors.copper,
              },
            },
            '& .MuiRadio-root .MuiSvgIcon-root': {
              fontSize: '18px',
            },
          }}
        />

        {formik.values.is_recurring !== BINARY_CONSTANTS.YES && (
          <Stack flexDirection="row" justifyContent="space-between" gap={2}>
            <SvgIconWrapper src={CurvedArrow} />
            <Typography variant="body2" alignSelf="self-end">
              Avoid a $7.00 fee that applies to each monthly installment for customers not enrolled
              in autopay.
            </Typography>
          </Stack>
        )}

        {renderAutoPayOptionText(formik.values.is_recurring)}
      </Stack>
    </FormControl>
  );
};

export default PaymentFrequency;
